import React, { useState, useEffect } from 'react';
import css from 'styled-jsx/css';
import classnames from 'classnames';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { Elements } from 'react-stripe-elements';
import scrollTo from '../../lib/js/scrollTo';
import uuidv1 from 'uuid/v1';
import { colors, fonts } from '../common/Theme';
import Router from 'next/router';
import Card from '../common/layout/Card';
import SupportFormToggle from '../common/elements/SupportFormToggle';

import TravelStep from './TravelForm';
import BeneficiariesStep from './Beneficiaries';
import SubscriberStep from './SubscriberStep';
import StripeAsyncProvider from './StripeAsyncProvider';

const formPadding = '12px';
const componentStyles = css`
  .subscription {
    flex-grow: 1;
    font-family: ${fonts.secondaryFont};
    background: white;
  }
  .paragraph {
    margin: 10px 0;
  }
  .subscription__footer {
    padding-left: ${formPadding};
  }
`;

const cardStyles = css.resolve`
  .subscription__card {
    background: ${colors.ywgPaleGreyTwo};
    padding-bottom: 24px;
    flex-grow: 0;
  }
  @media (min-width: 960px) {
    .subscription__card {
      margin: 0 auto;
      width: 960px;
    }
  }
`;
const welcomeStyle = css`
  .welcome__inbox {
    max-width: 200px;
    align-self: center;
  }
`;
const formStyle = css.resolve`
  .subscription__form {
    padding: 0 ${formPadding};
  }
`;
const supportButton = css.resolve`
  .button-inline {
    display: inline-block;
    margin-top: 24px;
    line-height: 1.2;
    text-align: left;
    text-decoration: underline;
    background: none;
    border: none;
    cursor: pointer;
  }

  .button-inline:hover,
  .button-inline:focus {
    text-decoration: none;
  }

  .button-inline::before {
    display: inline-block;
    margin-right: 5px;
    width: 15px;
    height: 15px;
    content: '';
    background: no-repeat url('/static/images/icons/support.svg');
    vertical-align: middle;
  }

  @media (max-width: 600px) {
    .button-inline {
      margin-top: 30px;
    }
  }
`;
function fillFromQuote(subscription, quote) {
  const firstBeneficiary =
    quote.beneficiaries && quote.beneficiaries[0]
      ? quote.beneficiaries[0]
      : subscription.subscriber;
  const firstStep =
    quote.steps && quote.steps[0] ? quote.steps[0] : subscription.travel;
  return {
    ...subscription,
    travel: {
      ...firstStep,
      startDate: format(
        Math.max(parseISO(firstStep.startDate), new Date()),
        'yyyy-MM-dd'
      ),
      endDate: ''
    },
    beneficiaries: (quote.beneficiaries || []).map(b => ({ ...b, email: '' })),
    subscriber: {
      ...subscription.subscriber,
      firstname:
        firstBeneficiary.firstname || subscription.subscriber.firstname,
      lastname: firstBeneficiary.firstname || subscription.subscriber.firstname,
      birthday: firstBeneficiary.birthday || subscription.subscriber.birthday,
      country: firstBeneficiary.country || subscription.subscriber.country
    }
  };
}
const newState = quote => {
  const emptyState = {
    step: 'travel',
    travel: {
      destination: '',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: ''
    },
    beneficiaries: [],
    subscriber: {
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      address: '',
      address_extra: '',
      postalcode: '',
      city: '',
      card_holder: ''
    },
    id: uuidv1(),
    manageSubscriber: true
  };
  return quote ? fillFromQuote(emptyState, quote) : emptyState;
};
const SubscribeForm = ({ countries, prices, apiKey, affiliateId, quote }) => {
  useEffect(() => {
    scrollTo();
  });
  const [subscription, setSubscription] = useState(newState(quote));
  const setQuotation = quotation => {
    setSubscription({
      ...subscription,
      quotation: quotation
    });
  };
  const setBeneficiaries = (beneficiaries, step) => {
    if (beneficiaries.length > 0) {
      setSubscription({
        ...subscription,
        beneficiaries,
        subscriber: {
          ...subscription.subscriber,
          ...beneficiaries[0],
          card_holder: `${beneficiaries[0].firstname} ${beneficiaries[0].lastname}`
        },
        step: step
      });
    } else {
      setSubscription({ ...subscription, beneficiaries, step: step });
    }
  };
  const setSubscriber = (subscriber, step) => {
    setSubscription({
      ...subscription,
      manageSubscriber: false,
      subscriber: subscriber,
      step: step
    });
  };
  const setTravel = (travel, step) => {
    setSubscription({
      ...subscription,
      travel: travel,
      step: step
    });
  };

  const cardClassNames = classnames('subscription__card', cardStyles.className);

  const steps = {
    travel: (
      <TravelStep
        travel={subscription.travel}
        setTravel={setTravel}
        countries={countries}
        className={classnames(formStyle.className, 'subscription__form')}
        onNext={travel => {
          setTravel(travel, 'beneficiaries');
        }}
      />
    ),
    beneficiaries: (
      <BeneficiariesStep
        quotation={subscription.quotation}
        travel={subscription.travel}
        setBeneficiaries={setBeneficiaries}
        beneficiaries={subscription.beneficiaries}
        setQuotation={setQuotation}
        prices={prices}
        className={classnames(formStyle.className, 'subscription__form')}
        onNext={beneficiaries => {
          setBeneficiaries(beneficiaries, 'subscriber');
        }}
        onPrev={beneficiaries => {
          setBeneficiaries(beneficiaries, 'travel');
        }}
      />
    ),
    subscriber: (
      <StripeAsyncProvider apiKey={apiKey}>
        <Elements>
          <SubscriberStep
            id={subscription.id}
            travel={subscription.travel}
            beneficiaries={subscription.beneficiaries}
            subscriber={subscription.subscriber}
            setQuotation={setQuotation}
            affiliateId={affiliateId}
            editBeneficiaries={subscriber => {
              setSubscriber(subscriber, 'beneficiaries');
            }}
            editTravel={subscriber => {
              setSubscriber(subscriber, 'travel');
            }}
            onDone={(subscriber, id) =>
              Router.push('/subscriptions/confirm?id=' + id)
            }
            countries={countries}
            className={classnames(formStyle.className, 'subscription__form')}
          />
        </Elements>
      </StripeAsyncProvider>
    ),
    welcome: (
      <>
        <img src="/static/images/ic-email.svg" className="welcome__inbox " />
        <h2>Dernière étape&nbsp;: Confirmez votre inscription&nbsp;!</h2>
        <p className="text">
          Nous venons de vous envoyer un email à&nbsp;:{' '}
          {subscription.subscriber.email}.
        </p>
        <p className="text">
          Nous venons de vous envoyer un email avec l’ensemble des pièces
          jointes nécessaires à la validation de votre souscription
          (Informations pré-contractuelles, bulletin de souscription, conditions
          générales…).
        </p>
        <p className="text">
          Dans ce mail, <strong>il faut que vous actionniez le bouton</strong>{' '}
          «&nbsp;Confirmer ma souscription&nbsp;» afin de la valider.
        </p>
        <p className="text">
          Une fois votre souscription traitée par notre service, nous mettrons
          en place l&apos;abonnement de paiement sur la carte bancaire que vous
          venez de nous fournir. À l&apos;issue de chaque paiement, vous
          recevrez un reçu par email.
        </p>
        <p className="text">
          ⚠️ Selon les contraintes de votre banque, vous pouvez être amené à
          confirmer votre paiement (autorisation 3DSecure) via notre prestataire
          de paiement Stripe.
        </p>
        <style jsx>{welcomeStyle}</style>
      </>
    )
  };
  return (
    <section className="subscription">
      <Card squared className={cardClassNames}>
        {steps[subscription.step]}
        <div className="subscription__footer">
          <SupportFormToggle
            className={classnames('button-inline', supportButton.className)}
            buttonText="Besoin d’aide"
          />
        </div>
      </Card>
      <style jsx>{componentStyles}</style>
      {cardStyles.styles}
      {formStyle.styles}
      {supportButton.styles}
    </section>
  );
};

export default SubscribeForm;
